import React, { useState } from "react";
import { Loader } from "lucide-react";
import {
  useConversionApprovals,
  useDecideConversionApproval,
  useRequestApprovalCode,
} from "../hooks/useTreasury";

const statusStyles = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-gray-100 text-gray-600",
  used: "bg-gray-100 text-gray-500",
};

// Anyone can approve or decline a request, the requester included. Approving
// first emails the signed-in admin a one-time code, typed in here to confirm;
// declining needs no code.
// An approval only allows the conversion; it does not run it. `onUse` fills the form above with it.
function ConversionApprovalsPanel({ onUse }) {
  const { data, isLoading } = useConversionApprovals();
  const { mutate: decide, isPending } = useDecideConversionApproval();
  const { mutate: requestCode, isPending: isSendingCode } = useRequestApprovalCode();
  // The request being approved right now and the code typed for it.
  const [codeFor, setCodeFor] = useState(null);
  const [code, setCode] = useState("");
  const startApprove = (uuid) => {
    requestCode(uuid, {
      onSuccess: () => {
        setCodeFor(uuid);
        setCode("");
      },
    });
  };
  const approvals = (data || []).filter((a) => a.status === "pending" || a.status === "approved");

  return (
    <div>
      <h2 className="mb-3 text-sm font-bold tracking-wider text-gray-500 uppercase">
        Approvals (conversions above the limit)
      </h2>
      <p className="mb-3 text-xs text-gray-500">
        An approval allows one conversion; it does not run by itself. Press Convert now on an approved row, get a quote, then confirm.
      </p>
      {isLoading ? (
        <p className="flex gap-2 items-center text-sm text-gray-500">
          Loading <Loader className="w-4 h-4 animate-spin" />
        </p>
      ) : approvals.length === 0 ? (
        <p className="text-sm text-gray-400">No pending or approved requests.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                {["Partner", "Conversion", "≈ USD", "Requested by", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {approvals.map((a) => (
                <tr key={a.uuid}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700">{a.partner}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {Number(a.amount)} {a.fromCurrency} → {a.toCurrency}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {a.amountUsd ? Number(a.amountUsd).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {a.source === "auto" ? (
                      <>
                        <span
                          className="px-2 py-0.5 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full"
                          title="The automatic top-up before a payout needed more than its per-conversion or daily cap, so it asked instead of skipping. The payout carried on through the next provider."
                        >
                          Automatic, over cap
                        </span>
                        {a.triggerPayoutReference && (
                          <span className="block mt-0.5 font-mono text-xs text-gray-400 break-all">
                            payout {a.triggerPayoutReference}
                          </span>
                        )}
                      </>
                    ) : (
                      a.requestedByAdminEmail || "-"
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[a.status]}`}>
                      {a.status}
                    </span>
                    {a.status === "approved" && a.expiresAt && (
                      <span className="block mt-0.5 text-xs text-gray-400">
                        until {new Date(a.expiresAt).toLocaleString()}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {a.status === "pending" && codeFor === a.uuid && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <input
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          inputMode="numeric"
                          placeholder="Code from your email"
                          className="px-3 py-1 w-44 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <button
                          onClick={() =>
                            decide(
                              { uuid: a.uuid, approve: true, code },
                              { onSuccess: () => setCodeFor(null) },
                            )
                          }
                          disabled={isPending || code.length < 4}
                          className="px-3 py-1 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          Confirm approval
                        </button>
                        <button
                          onClick={() => startApprove(a.uuid)}
                          disabled={isSendingCode}
                          className="text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
                        >
                          Resend code
                        </button>
                        <button onClick={() => setCodeFor(null)} className="text-xs font-medium text-gray-500 hover:text-gray-800">
                          Cancel
                        </button>
                        <button
                          onClick={() => decide({ uuid: a.uuid, approve: false }, { onSuccess: () => setCodeFor(null) })}
                          disabled={isPending}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {a.status === "pending" && codeFor !== a.uuid && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startApprove(a.uuid)}
                          disabled={isPending || isSendingCode}
                          className="px-3 py-1 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => decide({ uuid: a.uuid, approve: false })}
                          disabled={isPending}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {a.status === "approved" && onUse && (
                      <button
                        onClick={() => onUse(a)}
                        className="px-3 py-1 mr-2 text-xs font-medium text-white bg-[#013941] rounded-lg hover:bg-[#012B36]"
                      >
                        Convert now
                      </button>
                    )}
                    {a.status === "approved" && (
                      <button
                        onClick={() => decide({ uuid: a.uuid, approve: false })}
                        disabled={isPending}
                        title="Revoke this approval before it is used"
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                      >
                        Decline
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ConversionApprovalsPanel;
